import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Cog, FileText, Database, Cloud, Shield, LineChart, Brain, Briefcase, CheckCircle2, TrendingUp, Clock, Zap, HelpCircle, ChevronDown } from 'lucide-react';
import SEOHead from '../../components/SEOHead';

const keyMetrics = [
  {
    icon: TrendingUp,
    value: '95%',
    label: 'Manual Work Reduction',
    description: 'Average reduction in manual effort across implementations'
  },
  {
    icon: Clock,
    value: '60%',
    label: 'Faster Processing',
    description: 'Average improvement in workflow processing time'
  },
  {
    icon: Zap,
    value: '200+',
    label: 'Projects Delivered',
    description: 'Successful automation implementations across industries'
  },
  {
    icon: CheckCircle2,
    value: '99%',
    label: 'Client Satisfaction',
    description: 'Sustained partnerships and repeat engagements'
  }
];

const whatWeAutomate = [
  {
    title: 'Business Process Automation',
    icon: Cog,
    items: [
      'Workflow Orchestration: End-to-end process automation using Power Automate and custom solutions',
      'Document Processing: Intelligent extraction and processing of invoices, statements, and contracts',
      'Payment Processing: Automated commission calculations, reconciliation, and distribution systems',
      'Approval Workflows: Multi-level approval processes with automated routing and notifications'
    ]
  },
  {
    title: 'Platform Integration Automation',
    icon: Cloud,
    items: [
      'Microsoft Power Platform: PowerApps, Power Automate, Power BI comprehensive implementations',
      'CRM Automation: Salesforce, Dynamics, Zoho automated data sync and workflow optimization',
      'ERP Integration: SAP, Oracle automated data flows and process synchronization',
      'Custom API Development: Seamless connectivity between disparate business systems'
    ]
  },
  {
    title: 'Operational Automation',
    icon: LineChart,
    items: [
      'Report Generation: Automated creation and distribution of business reports and dashboards',
      'Data Validation: Real-time data quality checks and automated error correction protocols',
      'Notification Systems: Intelligent alerting for exceptions, deadlines, and performance metrics',
      'Compliance Monitoring: Automated tracking and reporting for regulatory requirements'
    ]
  }
];

const techStack = [
  {
    category: 'Microsoft Ecosystem',
    items: [
      { name: 'Power Platform', logo: '/tech-logos/power-bi.png' },
      { name: 'Azure Logic Apps', logo: '/tech-logos/azure-logo.svg' },
      { name: 'Office 365', logo: '/tech-logos/office-365.svg' }
    ]
  },
  {
    category: 'Specialized Platforms',
    items: [
      { name: 'Zoho', logo: '/tech-logos/zoho.png' },
      { name: 'Salesforce', logo: '/tech-logos/salesforce.svg' },
      { name: 'SAP', logo: '/tech-logos/sap.svg' }
    ]
  },
  {
    category: 'Integration Technologies',
    items: [
      { name: 'AWS', logo: '/tech-logos/aws.svg' },
      { name: 'Google Cloud', logo: '/tech-logos/google-cloud.svg' },
      { name: 'MongoDB', logo: '/tech-logos/mongodb.svg' }
    ]
  }
];

const industries = [
  {
    title: 'Healthcare & Pharmaceuticals',
    icon: FileText,
    applications: [
      'Clinical workflow automation',
      'Document processing',
      'Compliance reporting'
    ]
  },
  {
    title: 'Financial Services',
    icon: Database,
    applications: [
      'Transaction processing',
      'Risk assessment',
      'Regulatory reporting'
    ]
  },
  {
    title: 'Manufacturing & Supply Chain',
    icon: Cog,
    applications: [
      'Production planning',
      'Inventory management',
      'Quality control'
    ]
  },
  {
    title: 'Food Delivery & Logistics',
    icon: Cloud,
    applications: [
      'Order processing',
      'Route optimization',
      'Payment automation'
    ]
  }
];

const successStories = [
  {
    tag: 'Retail',
    title: 'UK Retail Pharmacy - Power Platform',
    metrics: [
      '95% reduction in manual effort',
      '60% faster reporting',
      'Real-time visibility'
    ],
    link: '/case-studies/retail-analytics'
  },
  {
    tag: 'Logistics',
    title: 'Food Delivery Platform - Payment Automation',
    metrics: [
      '100% automated processing',
      '3 days to 30 minutes',
      'Zero Excel dependency'
    ],
    link: '/case-studies/payment-automation'
  },
  {
    tag: 'Insurance',
    title: 'Insurance Brokerage - Document Processing',
    metrics: [
      '500+ daily statements automated',
      '95% reduction in manual work',
      'Real-time CRM updates'
    ],
    link: '/case-studies/insurance-automation'
  }
];

const methodology = [
  {
    title: 'Process Discovery & Analysis',
    description: 'Comprehensive assessment of current workflows and automation opportunities',
    icon: Brain
  },
  {
    title: 'Solution Architecture & Design',
    description: 'Detailed technical design and integration planning',
    icon: Cog
  },
  {
    title: 'Rapid Prototyping & Development',
    description: 'Agile development with continuous stakeholder feedback',
    icon: Cloud
  },
  {
    title: 'Testing & Quality Assurance',
    description: 'Rigorous testing of automation workflows and integrations',
    icon: Shield
  },
  {
    title: 'Deployment & Change Management',
    description: 'Smooth transition with comprehensive training and support',
    icon: LineChart
  },
  {
    title: 'Monitoring & Optimization',
    description: 'Continuous performance monitoring and process refinement',
    icon: TrendingUp
  }
];

const whyChooseUs = [
  {
    title: 'Proven Track Record',
    description: '200+ successful automation projects across industries',
    icon: Shield
  },
  {
    title: 'Technology Expertise',
    description: 'Deep expertise in leading automation platforms',
    icon: Brain
  },
  {
    title: 'Industry Knowledge',
    description: 'Domain-specific automation solutions',
    icon: Briefcase
  },
  {
    title: 'End-to-End Delivery',
    description: 'Comprehensive implementation and support',
    icon: Cloud
  }
];

const faqs = [
  {
    question: 'How long does automation implementation take?',
    answer: 'Our rapid implementation approach delivers automation solutions in 3-6 weeks depending on complexity. Simple workflow automation: 3-4 weeks. Platform integration and CRM automation: 4-6 weeks. Complex multi-system automation: 6-8 weeks. We\'re 60-80% faster than traditional consulting firms through agile methodologies and lean teams.'
  },
  {
    question: 'What makes Agility\'s implementation approach different?',
    answer: 'We deploy senior engineering teams directly on your project no layers of account managers or junior staff. Our agile squads ship production-ready automation workflows in weeks, not months, with continuous delivery and real-time collaboration. Every week you see working automation, not presentations.'
  },
  {
    question: 'Do you provide support after deployment?',
    answer: 'Yes, we provide 24/7 post-implementation support including continuous monitoring, optimization, and maintenance. Our support packages ensure your automation solutions continue delivering value with proactive updates and issue resolution.'
  },
  {
    question: 'What types of processes can be automated?',
    answer: 'We automate a wide range of business processes including document processing, workflow orchestration, payment automation, CRM integrations, report generation, data validation, and compliance monitoring. If it\'s a repetitive manual process, we can automate it.'
  }
];

const AutomationSolution = () => {
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);
  return (
    <>
      <SEOHead
        title="Business Process Automation Solutions | Agility"
        description="End-to-end automation consulting services. RPA, workflow automation, and intelligent process optimization. 95% reduction in manual work."
        keywords="process automation, RPA, workflow automation, business automation, intelligent automation, robotic process automation"
        url="https://www.agilitytech.ai/solutions/automation"
      />
      <div className="pt-20">
      {/* Hero Section */}
      <section className="relative py-24 bg-gradient-to-r from-blue-900 to-black overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1563986768609-322da13575f3?auto=format&fit=crop&q=80"
            alt="Automation Banner"
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-transparent" />
        </div>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-4xl">
            <div className="inline-flex items-center px-4 py-2 bg-teal-500/10 rounded-full border border-teal-500/20 mb-6">
              <span className="text-teal-400 text-sm font-medium">Intelligent Automation</span>
            </div>
            <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight">
              Intelligent Automation Solutions
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed mb-8 max-w-3xl">
              Transform your business processes through cutting-edge automation that eliminates manual 
              tasks, reduces errors, and accelerates operational efficiency across your entire organization.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link
                to="/contact"
                className="bg-teal-500 text-white px-8 py-4 rounded-lg text-lg font-medium 
                  hover:bg-teal-400 transition-all duration-300 inline-flex items-center space-x-2
                  shadow-lg hover:shadow-xl transform hover:-translate-y-1"
              >
                <span>Schedule Your Automation Assessment</span>
                <ArrowRight className="h-5 w-5" />
              </Link>
              <button
                className="bg-white/10 text-white px-8 py-4 rounded-lg text-lg font-medium 
                  hover:bg-white/20 transition-all duration-300 inline-flex items-center space-x-2
                  border border-white/20 hover:border-white/30"
              >
                <span>Download Solutions Guide</span>
                <ArrowRight className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Key Metrics Section */}
      <section className="py-16 bg-black">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {keyMetrics.map((metric, index) => (
              <div key={index} className="bg-gray-900/50 backdrop-blur-sm rounded-xl p-6 
                border border-gray-800/50 hover:bg-gray-900/70 transition-all duration-300 text-center">
                <div className="w-12 h-12 bg-teal-500/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <metric.icon className="h-6 w-6 text-teal-400" />
                </div>
                <div className="text-3xl font-bold text-white mb-2">{metric.value}</div>
                <div className="text-teal-400 font-medium mb-2">{metric.label}</div>
                <p className="text-gray-400 text-sm">{metric.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What We Automate Section */}
      <section className="py-20 bg-black">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-12 text-center">
            What We Automate
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {whatWeAutomate.map((category, index) => (
              <div
                key={index}
                className="bg-gray-900/50 backdrop-blur-sm rounded-xl p-8 
                  border border-gray-800/50 hover:bg-gray-900/70 transition-all duration-300
                  transform hover:-translate-y-1"
              >
                <div className="w-12 h-12 bg-teal-500/10 rounded-xl flex items-center 
                  justify-center mb-6">
                  <category.icon className="h-6 w-6 text-teal-400" />
                </div>
                <h3 className="text-xl font-bold text-white mb-4">{category.title}</h3>
                <ul className="space-y-3">
                  {category.items.map((item, idx) => (
                    <li key={idx} className="flex items-start space-x-3 text-gray-300">
                      <CheckCircle2 className="h-5 w-5 text-teal-400 flex-shrink-0 mt-1" />
                      <span className="text-sm leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Technology Stack Section */}
      <section className="py-20 bg-gray-900">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-12 text-center">
            Technology Stack
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {techStack.map((category, index) => (
              <div key={index} className="space-y-6">
                <h3 className="text-xl font-bold text-teal-400 mb-4 text-center">{category.category}</h3>
                <div className="grid grid-cols-1 gap-4">
                  {category.items.map((item, idx) => (
                    <div
                      key={idx}
                      className="flex items-center space-x-3 bg-gray-800/50 rounded-lg p-4
                        border border-gray-700/50 hover:bg-gray-800/70 transition-all duration-300"
                    >
                      <img
                        src={item.logo}
                        alt={`${item.name} logo`}
                        className="h-8 w-8 object-contain"
                      />
                      <span className="text-gray-300 font-medium">{item.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Industry Applications Section */}
      <section className="py-20 bg-black">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-12 text-center">
            Industry Applications
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {industries.map((industry, index) => (
              <div
                key={index}
                className="bg-gray-900/50 backdrop-blur-sm rounded-xl p-8 
                  border border-gray-800/50 hover:bg-gray-900/70 transition-all duration-300
                  transform hover:-translate-y-1"
              >
                <div className="w-12 h-12 bg-teal-500/10 rounded-xl flex items-center 
                  justify-center mb-6">
                  <industry.icon className="h-6 w-6 text-teal-400" />
                </div>
                <h3 className="text-xl font-bold text-white mb-4">{industry.title}</h3>
                <ul className="space-y-2">
                  {industry.applications.map((app, idx) => (
                    <li key={idx} className="flex items-center space-x-2 text-gray-300">
                      <ArrowRight className="h-4 w-4 text-teal-400" />
                      <span className="text-sm">{app}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Success Stories Section */}
      <section className="py-20 bg-gray-900">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-12 text-center">
            Featured Success Stories
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {successStories.map((story, index) => (
              <div
                key={index}
                className="bg-gray-800/50 rounded-xl p-8 border border-gray-700/50
                  hover:bg-gray-800/70 transition-all duration-300 transform hover:-translate-y-1"
              >
                <div className="text-sm text-teal-400 font-semibold mb-4">{story.tag}</div>
                <h3 className="text-xl font-bold text-white mb-6">{story.title}</h3>
                <div className="space-y-3 mb-6">
                  {story.metrics.map((metric, idx) => (
                    <div key={idx} className="flex items-center space-x-2 text-gray-300">
                      <CheckCircle2 className="h-4 w-4 text-green-400" />
                      <span className="text-sm">{metric}</span>
                    </div>
                  ))}
                </div>
                <Link
                  to={story.link}
                  className="inline-flex items-center text-teal-400 hover:text-teal-300
                    transition-colors font-medium"
                >
                  <span>Read Full Case Study</span>
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Methodology Section */}
      <section className="py-20 bg-black">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-12 text-center">
            Our Methodology
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {methodology.map((step, index) => (
              <div
                key={index}
                className="bg-gray-900/50 backdrop-blur-sm rounded-xl p-6 
                  border border-gray-800/50 hover:bg-gray-900/70 transition-all duration-300"
              >
                <div className="flex items-center space-x-4 mb-4">
                  <div className="w-8 h-8 bg-teal-500/10 rounded-full flex items-center 
                    justify-center text-teal-400 font-bold">
                    {index + 1}
                  </div>
                  <div className="w-8 h-8 bg-teal-500/10 rounded-lg flex items-center justify-center">
                    <step.icon className="h-5 w-5 text-teal-400" />
                  </div>
                </div>
                <h3 className="text-lg font-bold text-white mb-3">{step.title}</h3>
                <p className="text-gray-300 text-sm leading-relaxed">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Agility.AI Section */}
      <section className="py-20 bg-gray-900">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-12 text-center">
            Why Choose Agility.AI
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {whyChooseUs.map((reason, index) => (
              <div
                key={index}
                className="bg-gray-800/50 rounded-xl p-8 border border-gray-700/50
                  hover:bg-gray-800/70 transition-all duration-300 text-center"
              >
                <div className="w-12 h-12 bg-teal-500/10 rounded-xl flex items-center 
                  justify-center mb-6 mx-auto">
                  <reason.icon className="h-6 w-6 text-teal-400" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3">{reason.title}</h3>
                <p className="text-gray-300 text-sm leading-relaxed">{reason.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-black">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <HelpCircle className="h-12 w-12 text-teal-400 mx-auto mb-4" />
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                Frequently Asked Questions
              </h2>
              <p className="text-xl text-gray-400">
                Everything you need to know about our automation implementation process
              </p>
            </div>
            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <div
                  key={index}
                  className="bg-gray-900/50 backdrop-blur-sm rounded-xl border border-gray-800/50 overflow-hidden"
                >
                  <button
                    onClick={() => setOpenFaqIndex(openFaqIndex === index ? null : index)}
                    className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-gray-900/70 transition-all duration-300"
                  >
                    <span className="text-lg font-semibold text-white pr-8">{faq.question}</span>
                    <ChevronDown
                      className={`h-5 w-5 text-teal-400 flex-shrink-0 transition-transform duration-300 ${
                        openFaqIndex === index ? 'rotate-180' : ''
                      }`}
                    />
                  </button>
                  {openFaqIndex === index && (
                    <div className="px-6 pb-5">
                      <p className="text-gray-300 leading-relaxed">{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-900 to-black">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="max-w-3xl mx-auto">
            <div className="w-16 h-16 bg-teal-500/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <Cog className="h-8 w-8 text-teal-400" />
            </div>
            <h2 className="text-4xl font-bold text-white mb-6">
              Ready to Automate Your Operations?
            </h2>
            <p className="text-xl text-gray-300 mb-8 leading-relaxed">
              Transform Manual Processes into Intelligent Workflows. Contact our automation specialists 
              to discover how intelligent automation can revolutionize your business operations.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center space-x-3 bg-teal-500 text-white px-8 py-4 
                rounded-lg text-lg font-medium hover:bg-teal-400 transition-all duration-300 group
                shadow-lg hover:shadow-xl transform hover:-translate-y-1"
            >
              <span>Get Started Today</span>
              <ArrowRight className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </section>
    </div>
    </>
  );
};

export default AutomationSolution;